import {
    BORDER_STYLES, DEFAULT_BORDER_WIDTH, DEFAULT_NODE_COLOR, SHAPES, TEXT_NODE_TYPES, TRANSPARENT_COLOR,
} from '../Shapes/shapeCatalog';
import { sizeOfNode } from '../MapActions/newElements';
import {
    DEFAULT_FONT_SIZE, DEFAULT_FONT_STYLE, DEFAULT_FONT_WEIGHT, DEFAULT_TEXT_ALIGN, DEFAULT_TEXT_DECORATION,
} from '../TextBox/textStyle';
import { normalizeHex, parseFill, toFillColor } from './colors';

const DEFAULT_TEXT_COLOR = '#000000';
// Lower case like every colour the panel shows, so the picker marks it.
const DEFAULT_FILL = DEFAULT_NODE_COLOR.toLowerCase();
// Fill of nodes saved before shapes had a colour of their own (they draw white).
const LEGACY_FILL = '#ffffff';
const FULL_OPACITY = 100;
const MAP_COLOR_LIMIT = 10;

// Panel fields by what saving them touches. Fill opacity is saved inside nodeBg as rgba(). Border (D-045) and text
// style (D-047) fields carry the names they are saved under in `data` and travel to collaborators as one socket
// message per group; the border colour is the creator's colour, not a panel field.
const FILL_FIELDS = ['isFilled', 'fill', 'fillAlpha'];
const DATA_FIELD_GROUPS = {
    border: ['borderWidth', 'borderStyle'],
    textStyle: ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration', 'textAlign'],
};
const DATA_FIELDS = Object.values(DATA_FIELD_GROUPS).flat();
const STYLE_FIELDS = ['label', ...FILL_FIELDS, 'textColor', 'type', ...DATA_FIELDS];
const SIZE_FIELDS = ['width', 'height'];
const POSITION_FIELDS = ['x', 'y'];
const hasAny = (touched, fields) => fields.some((field) => touched.has(field));

const NODE_TYPES = [...TEXT_NODE_TYPES, ...SHAPES];

/** The name of a node type, as the shape dropdown and sidebar call it. */
export const typeLabelOf = (type) => NODE_TYPES.find((candidate) => candidate.type === type)?.label ?? 'Node';

/** The node as panel values. A colour the panel cannot read shows a fallback but is only rewritten if edited. */
export const readValues = (node) => {
    const {
        label, nodeBg, textBg, borderWidth, borderStyle, fontFamily, fontSize, fontWeight, fontStyle, textDecoration, textAlign,
    } = node.data ?? {};
    const { width, height } = sizeOfNode(node);
    const fill = parseFill(nodeBg);
    return {
        label: label ?? '',
        isFilled: nodeBg !== TRANSPARENT_COLOR,
        fill: fill?.hex ?? (nodeBg ? DEFAULT_FILL : LEGACY_FILL),
        fillAlpha: fill?.alpha ?? FULL_OPACITY,
        textColor: normalizeHex(textBg, DEFAULT_TEXT_COLOR),
        fontFamily: fontFamily ?? '',
        fontSize: fontSize || DEFAULT_FONT_SIZE,
        fontWeight: fontWeight || DEFAULT_FONT_WEIGHT,
        fontStyle: fontStyle || DEFAULT_FONT_STYLE,
        textDecoration: textDecoration || DEFAULT_TEXT_DECORATION,
        textAlign: textAlign || DEFAULT_TEXT_ALIGN,
        borderStyle: borderStyle || BORDER_STYLES.solid,
        borderWidth: borderWidth || DEFAULT_BORDER_WIDTH,
        type: node.type,
        width: Math.round(width),
        height: Math.round(height),
        x: Math.round(node.position.x),
        y: Math.round(node.position.y),
    };
};

/** With the aspect ratio locked, a new width or height brings the other along. */
export const withAspect = (patch, { width, height }) => {
    if ('width' in patch && width) return { ...patch, height: Math.round((patch.width * height) / width) };
    if ('height' in patch && height) return { ...patch, width: Math.round((patch.height * width) / height) };
    return patch;
};

/** Why the last of fill and border cannot be switched off. */
export const NEEDS_PAINT = 'A shape needs a fill or a border';

/**
 * Which of fill and border is the only one switched on, so it cannot be switched off: a shape with neither would
 * vanish from the map. Null while both or neither are on.
 */
export const lastPaint = ({ isFilled, borderStyle }) => {
    const hasBorder = borderStyle !== BORDER_STYLES.none;
    if (isFilled === hasBorder) return null;
    return isFilled ? 'fill' : 'border';
};

/**
 * The node with the `touched` panel fields written into it: data.label, data.nodeBg, data.textBg, the border and
 * text style fields (D-045, D-047), type; size like the resizer; position like a drag. Untouched fields are left alone.
 */
export const applyValues = (node, values, touched) => {
    const data = { ...node.data };
    if (touched.has('label')) data.label = values.label;
    if (hasAny(touched, FILL_FIELDS)) data.nodeBg = values.isFilled ? toFillColor(values.fill, values.fillAlpha) : TRANSPARENT_COLOR;
    if (touched.has('textColor')) data.textBg = values.textColor;
    DATA_FIELDS.filter((field) => touched.has(field)).forEach((field) => {
        data[field] = values[field];
    });
    let next = { ...node, data };
    if (touched.has('type')) next.type = values.type;
    if (hasAny(touched, SIZE_FIELDS)) {
        const { width, height } = values;
        next = { ...next, width, height, style: { ...node.style, width, height }, data: { ...next.data, width, height } };
    }
    if (hasAny(touched, POSITION_FIELDS)) next.position = { x: values.x, y: values.y };
    return next;
};

/**
 * The partial node payloads to save, one per kind of change. A style change sends the whole `data`, as the node's
 * text box does, so the border and text style fields are kept however the backend merges `data`.
 */
export const savePayloads = (node, touched) => [
    hasAny(touched, STYLE_FIELDS) && { data: { ...node.data }, type: node.type },
    hasAny(touched, SIZE_FIELDS) && { style: { width: node.style.width, height: node.style.height } },
    hasAny(touched, POSITION_FIELDS) && { position: node.position },
].filter(Boolean);

/** The node-update socket messages: one property each, the way collaborators' clients read them. */
export const socketUpdates = (node, touched, documentId) => {
    const base = { id: node.id, documentId };
    const updates = [];
    if (touched.has('label')) updates.push({ ...base, name: node.data.label });
    if (hasAny(touched, FILL_FIELDS)) updates.push({ ...base, bg: node.data.nodeBg });
    if (touched.has('textColor')) updates.push({ ...base, textBg: node.data.textBg });
    if (touched.has('type')) updates.push({ ...base, type: node.type });
    Object.entries(DATA_FIELD_GROUPS).filter(([, fields]) => hasAny(touched, fields)).forEach(([group, fields]) => {
        const saved = fields.filter((field) => node.data[field] !== undefined);
        updates.push({ ...base, [group]: Object.fromEntries(saved.map((field) => [field, node.data[field]])) });
    });
    if (hasAny(touched, SIZE_FIELDS)) {
        // A resize message carries the position too, so it also covers a move.
        updates.push({ ...base, style: { width: node.style.width, height: node.style.height }, nodePosition: node.position });
    } else if (hasAny(touched, POSITION_FIELDS)) {
        updates.push({ ...base, position: node.position });
    }
    return updates;
};

/** The distinct fill colours on the map, most used first, for the colour picker's "On this map". */
export const mapColors = (nodes) => {
    const counts = new Map();
    nodes.forEach((node) => {
        const hex = normalizeHex(node.data?.nodeBg, null);
        if (hex) counts.set(hex, (counts.get(hex) || 0) + 1);
    });
    return [...counts].sort((a, b) => b[1] - a[1]).slice(0, MAP_COLOR_LIMIT).map(([hex]) => hex);
};
